import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Panel, PanelGroup } from 'react-bootstrap';
import { DISPUTE_FEE_TYPE, NONRKPAY_DISPUTE_REASON_STATUS } from '@constants/index';
import utils from "@utils";
import './DisputesMenu.scss';
import DisputeReasonCard from './DisputeReasonCard';
import BillingDetail from './BillingDetail';
import { useDisputeContext } from '../../context/DisputeContext';

const DisputesMenu = ({ dispute, disputeId, user, className, isReadOnly }) => {
	const { lang } = global;
	const {
		openSection,
		openDisputeReason,
		handleOpenSection,
		handleOpenDisputeReason,
		closeDisputeReason,
	} = useDisputeContext();
	const DISPUTE_REASON_TYPES = useRef({
		HoursIncorrectDisputes: lang.t('Hours Incorrect'),
		BillingDetailsIncorrectDisputes: lang.t('Billing Details Incorrect'),
		RateIncorrectDisputes: lang.t('Rate Incorrect'),
		JobNotWorkedDisputes: lang.t('Job Not Worked'),
		FeesAndTaxesIncorrectDisputes: lang.t('Fees And Taxes Incorrect'),
		JobAlreadyPaidDisputes: lang.t('Already Paid For'),
		MiscFeesIncorrectDisputes: lang.t('Officer Misc Fees Incorrect'),
		OtherDisputes: lang.t('Other'),
	}).current;

	const DISPUTE_FEE_TYPES = useRef({
		[DISPUTE_FEE_TYPE.VEHICLE]: lang.t('Vehicle'),
		[DISPUTE_FEE_TYPE.ASSET]: lang.t('Assets / Equipment'),
		[DISPUTE_FEE_TYPE.AGENCY]: lang.t('Agency'),
		[DISPUTE_FEE_TYPE.COORDINATOR]: lang.t('Coordinator'),
		[DISPUTE_FEE_TYPE.SERVICE]: lang.t('Service'),
		[DISPUTE_FEE_TYPE.TAXES]: lang.t('Taxes'),
		[DISPUTE_FEE_TYPE.MISSING_FEES]: lang.t('Missing Fees'),
		[DISPUTE_FEE_TYPE.OTHER]: lang.t('Other'),
		[DISPUTE_FEE_TYPE.OFFICER_MISC_FEE]: lang.t('Officer Misc Fees'),
	}).current;

	const { totalDisputeReasons, totalCompletedDisputeReasons, isDisputeCompleted } = useMemo(() =>
		utils.getNonRKPayDisputeAmendmentStatus(dispute)
		, [dispute]);

	const handleSaveDisputeReason = () => {
		closeDisputeReason();
	}

	const onCancelDisputeReason = () => {
		closeDisputeReason();
	}

	return (
		<div className={classNames("disputes-menu-container", className)}>
			{!isReadOnly && (
				<>
					<h4 className="text-center margin-bottom-16">
						{lang.t('Invoice Dispute(s)')}
					</h4>
					{!isDisputeCompleted && (
						<div className="alert alert-info">
							<i className="glyphicon glyphicon-info-sign" />
							&nbsp;
							{lang.t(
								"An invoice can only be sent back to the client once all disputes reasons have been addressed. ",
							)}
						</div>
					)}
					{isDisputeCompleted && (
						<div className="alert alert-success">
							<i className="rkf rkf-check-mark-o" />
							&nbsp;
							{lang.t(
								"All disputes are resolved; the invoice is ready to be sent.",
							)}
							<p>{lang.t("Subsequent edits on the invoice as a whole can be made prior to sending, if applicable.")}</p>
						</div>
					)}
					<p>
						{lang.t("dynamic.Dispute reasons have been addressed", {
							completed: totalCompletedDisputeReasons,
							total: totalDisputeReasons,
						})}
					</p>
				</>
			)}
			<PanelGroup>
				{Object.keys(dispute).map((key) => {
					const disputeReasons = dispute[key];
					if (disputeReasons.length === 0) {
						return null;
					}

					const expanded = openSection === key;
					const title = DISPUTE_REASON_TYPES[key];
					const completedDisputeReasons = disputeReasons.filter(reason => reason.Status !== NONRKPAY_DISPUTE_REASON_STATUS.CREATED).length;

					return (
						<Panel
							key={key}
							expanded={expanded}
							className='simple-collapse border-none'
							onToggle={() => handleOpenSection(key)}
						>
							<Panel.Heading
								className={classNames('heading-border', expanded ? 'panel-expanded' : 'panel-collapsed')}>
								<Panel.Title toggle>
									<div className='title-content'>
										<div className='title-text'>
											<div className='main-title'>
												{title}
											</div>
											<div className='sub-title'>
												{lang.t('dynamic.DisputeReasonGroupSubtitle', {
													total: disputeReasons.length,
													completed: completedDisputeReasons,
												})}
											</div>
										</div>
										<div
											className={classNames(
												"margin-left-20 title-expand-icon",
												expanded ? "panel-expanded" : "panel-collapsed",
											)}
										>
											<i className="rkf rkf-chevron-up" />
										</div>
									</div>
								</Panel.Title>
							</Panel.Heading>
							{disputeReasons.map(disputeReason => {
								const formattedDate = utils.formatDate(
									new Date(`${disputeReason.JobDate}Z`),
									"en-US",
									user.TimeZoneId,
									user.UserPreferences === "useMilitaryTime" ? "dt-short-mil" : "dt-short",
								)
								return (
									<Panel.Collapse key={disputeReason.Id}>
										<Panel.Body className='no-border padding-0'>
											<DisputeReasonCard
												title={title}
												isReadOnly={isReadOnly}
												disputeId={disputeId}
												disputeReason={disputeReason}
												onCancel={onCancelDisputeReason}
												onSubmit={handleSaveDisputeReason}
												expanded={openDisputeReason === disputeReason.Id}
												onClick={() => handleOpenDisputeReason(disputeReason.Id)}
											>
												{/** If a dispute reason has a job id, it means that it could be one of the following types:
												 * - HoursIncorrectDisputes
												 * - RateIncorrectDisputes
												 * - JobNotWorkedDisputes
												 * - JobAlreadyPaidDisputes
												 */}
												{disputeReason.JobId && (
													<p className="margin-bottom-0">
														{`${lang.t('Job ID')} ${disputeReason.JobId} / ${formattedDate} / ${disputeReason.OfficerName ?? lang.t('TBD')}`}
													</p>
												)}
												{key === 'HoursIncorrectDisputes' && (
													<p className="margin-bottom-0">
														{`${lang.t('Current Hours')}: ${disputeReason.HoursWorked}`}
													</p>
												)}
												{key === 'RateIncorrectDisputes' && (
													<p className="margin-bottom-0">
														{`${lang.t('Current Rate')}: ${utils.currencyFormatter(disputeReason.Rate)}`}
													</p>
												)}
												{key === "BillingDetailsIncorrectDisputes" && (
													<>
														{disputeReason.From && (
															<BillingDetail
																title={lang.t('From')}
																name={disputeReason.From?.Name}
																address={disputeReason.From?.Address}
																email={disputeReason.From?.Email}
																contact={disputeReason.From?.ContactName}
															/>
														)}
														{disputeReason.To && (
															<BillingDetail
																title={lang.t('To')}
																name={disputeReason.To?.Name}
																address={disputeReason.To?.Address}
																email={disputeReason.To?.Email}
																contact={disputeReason.To?.ContactName}
															/>
														)}
													</>
												)}
												{Boolean(disputeReason.FeeType) && (
													<p className="margin-bottom-0">
														{`${lang.t('Fee Type')}: ${DISPUTE_FEE_TYPES[disputeReason.FeeType]}`}
													</p>
												)}
												{disputeReason.DisputeComment && (
													<p className="margin-bottom-0">
														{`${lang.t('Additional Comment')}: ${disputeReason.DisputeComment}`}
													</p>
												)}
											</DisputeReasonCard>
										</Panel.Body>
									</Panel.Collapse>
								)
							})}
						</Panel>
					)
				})}
			</PanelGroup>
		</div >
	);
};

DisputesMenu.propTypes = {
	disputeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	dispute: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,
	className: PropTypes.string,
	isReadOnly: PropTypes.bool,
};

DisputesMenu.defaultProps = {
	className: '',
	isReadOnly: false,
};

const mapStateToProps = (state) => ({
	user: state.app.user,
});

export default connect(mapStateToProps)(DisputesMenu);
